import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PrinterIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { PrintLocationDialog } from "@/features/timesheetFlow/components/PrintLocationDialog";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userHasGroup, userIsBuero } from "@/lib/auth";
import { formatDate, formatRelative } from "@/lib/format";
import { formatAttachmentSize } from "@/lib/mailAttachments";
import {
  fetchSchwarzesBrettTree,
  type SchwarzesBrettFile,
  type SchwarzesBrettFolder,
  type SchwarzesBrettTree,
} from "@/lib/schwarzesBrett";
import {
  countRecentFiles,
  displayFileName,
  fileKindMeta,
  groupByFolder,
  isRecentFile,
  searchFiles,
} from "@/pages/schwarzesBrett/fileKind";
import { FilePreviewDialog } from "@/pages/schwarzesBrett/FilePreviewDialog";
import { folderIconFor } from "@/pages/schwarzesBrett/folderIcons";
import { canPrintFile, printSchwarzesBrettFile } from "@/pages/schwarzesBrett/print";

/** Aktionen, die auf jede Dateizeile wirken (Vorschau, Drucken). */
interface FileActions {
  onOpen: (file: SchwarzesBrettFile) => void;
  onPrint: (file: SchwarzesBrettFile) => void;
  /** Büro/App-Admins: externe SharePoint-/Office-Links anzeigen. */
  canOpenInSharePoint: boolean;
}

export function SchwarzesBrettIndexPage() {
  const { user } = useCurrentUser();
  // Inhalte werden in SharePoint gepflegt – der direkte Absprung dorthin ist Sache des Büros.
  const canOpenInSharePoint = userIsBuero(user) || userHasGroup(user, "App-Admins");
  const [tree, setTree] = useState<SchwarzesBrettTree | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFolderId = searchParams.get("ordner");
  const setSelectedFolderId = useCallback(
    (id: string | null) => {
      setSearchParams(id ? { ordner: id } : {}, { replace: false });
    },
    [setSearchParams],
  );
  const [previewFile, setPreviewFile] = useState<SchwarzesBrettFile | null>(null);
  const [printFile, setPrintFile] = useState<SchwarzesBrettFile | null>(null);

  const fileActions: FileActions = useMemo(
    () => ({ onOpen: setPreviewFile, onPrint: setPrintFile, canOpenInSharePoint }),
    [canOpenInSharePoint],
  );

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const data = await fetchSchwarzesBrettTree(refresh);
      setTree(data);
    } catch (err) {
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : "Schwarzes Brett konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const newDays = tree?.new_days ?? 14;
  const trimmedQuery = query.trim();
  const searching = trimmedQuery.length > 0;

  const selectedFolder = useMemo(
    () => tree?.folders.find((f) => f.id === selectedFolderId) ?? null,
    [tree, selectedFolderId],
  );

  const searchGroups = useMemo(() => {
    if (!tree || !searching) return [];
    return groupByFolder(searchFiles(tree, trimmedQuery), tree.folders);
  }, [tree, searching, trimmedQuery]);

  const searchHits = useMemo(
    () => searchGroups.reduce((acc, g) => acc + g.files.length, 0),
    [searchGroups],
  );

  const actions = (
    <>
      {canOpenInSharePoint && tree?.root?.web_url ? (
        <a
          href={tree.root.web_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-card px-4 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque transition hover:bg-fill-tertiary"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
          In SharePoint öffnen
        </a>
      ) : null}
      <Button
        type="button"
        variant="secondary"
        loading={refreshing}
        onClick={() => void load(true)}
        leftIcon={<ArrowPathIcon className="h-4 w-4" aria-hidden />}
        title="Frisch aus SharePoint laden"
      >
        Aktualisieren
      </Button>
    </>
  );

  return (
    <AppShell
      title="Schwarzes Brett"
      subtitle="Aushänge, Formulare und Informationen für alle im Unternehmen – gepflegt in SharePoint."
      actions={actions}
      back={selectedFolder && !searching ? { to: "/schwarzes-brett", label: "Alle Ordner" } : undefined}
    >
      {error ? (
        <Alert variant="error" title="Schwarzes Brett konnte nicht geladen werden">
          {error}
        </Alert>
      ) : null}

      {loading && !tree ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Aushänge werden geladen …</span>
        </div>
      ) : null}

      {tree ? (
        <>
          <label className="relative block">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary"
              aria-hidden
            />
            <input
              type="search"
              className="eh-input w-full pl-9"
              placeholder={`Suchen in ${tree.file_count} Dateien – z. B. Telefonliste, Urlaubsantrag …`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Schwarzes Brett durchsuchen"
            />
          </label>

          {tree.truncated ? (
            <Alert variant="warning">
              Es werden nicht alle Dateien angezeigt (Limit erreicht). Bitte den Ordner in SharePoint aufräumen.
            </Alert>
          ) : null}

          {searching ? (
            <SearchResults
              groups={searchGroups}
              hits={searchHits}
              newDays={newDays}
              actions={fileActions}
              onOpenFolder={(id) => {
                setQuery("");
                setSelectedFolderId(id);
              }}
            />
          ) : selectedFolder ? (
            <FolderView
              folder={selectedFolder}
              newDays={newDays}
              actions={fileActions}
              onBack={() => setSelectedFolderId(null)}
            />
          ) : (
            <Overview
              tree={tree}
              newDays={newDays}
              actions={fileActions}
              onSelectFolder={setSelectedFolderId}
            />
          )}

          <p className="text-xs text-label-tertiary">
            Stand: {tree.generated_at ? formatRelative(tree.generated_at) || formatDate(tree.generated_at) : "–"}
            {" · "}Inhalte werden ausschließlich in SharePoint gepflegt und hier alle{" "}
            {Math.max(1, Math.round(tree.cache_ttl_seconds / 60))} Minuten aktualisiert.
          </p>
        </>
      ) : null}

      <FilePreviewDialog
        file={previewFile}
        canOpenInSharePoint={canOpenInSharePoint}
        onClose={() => setPreviewFile(null)}
      />

      <PrintLocationDialog
        open={Boolean(printFile)}
        onClose={() => setPrintFile(null)}
        onPrint={async (locationId, copies) => {
          if (!printFile) return;
          await printSchwarzesBrettFile(printFile, locationId, copies);
        }}
      />
    </AppShell>
  );
}

/* ------------------------------------------------------------------------ */

function Overview({
  tree,
  newDays,
  actions,
  onSelectFolder,
}: {
  tree: SchwarzesBrettTree;
  newDays: number;
  actions: FileActions;
  onSelectFolder: (id: string) => void;
}) {
  const recent = tree.recent || [];
  return (
    <div className="space-y-6">
      {recent.length ? (
        <Card
          title={
            <span className="inline-flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-accent" aria-hidden />
              Neu in den letzten {newDays} Tagen
            </span>
          }
          bodyClassName="px-2 py-2"
        >
          <FileList files={recent} newDays={newDays} showFolder actions={actions} />
        </Card>
      ) : null}

      {tree.folders.length ? (
        <section aria-label="Ordner">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {tree.folders.map((folder) => (
              <FolderTile
                key={folder.id}
                folder={folder}
                newCount={countRecentFiles(folder.files, newDays)}
                onClick={() => onSelectFolder(folder.id)}
              />
            ))}
          </div>
        </section>
      ) : (
        <Alert variant="info">Im Schwarzen Brett sind noch keine Ordner vorhanden.</Alert>
      )}

      {tree.loose_files.length ? (
        <Card title="Weitere Dateien" description="Direkt im Hauptordner abgelegt" bodyClassName="px-2 py-2">
          <FileList files={tree.loose_files} newDays={newDays} actions={actions} />
        </Card>
      ) : null}
    </div>
  );
}

function FolderTile({
  folder,
  newCount,
  onClick,
}: {
  folder: SchwarzesBrettFolder;
  newCount: number;
  onClick: () => void;
}) {
  const Icon = folderIconFor(folder.name);
  return (
    <button
      type="button"
      onClick={onClick}
      className="eh-card group flex min-h-[7.5rem] flex-col justify-between p-4 text-left transition hover:-translate-y-0.5 hover:shadow-ios-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
          <Icon className="h-6 w-6" aria-hidden />
        </span>
        {newCount > 0 ? <NewBadge label={`${newCount} neu`} /> : null}
      </div>
      <div className="mt-3 min-w-0">
        <div className="flex items-center gap-1">
          <span className="truncate text-sm font-semibold text-label">{folder.name}</span>
          <ChevronRightIcon
            className="h-4 w-4 shrink-0 text-label-tertiary transition group-hover:translate-x-0.5"
            aria-hidden
          />
        </div>
        <p className="mt-0.5 text-xs text-label-secondary">
          {folder.file_count === 1 ? "1 Datei" : `${folder.file_count} Dateien`}
          {folder.latest_modified_at ? ` · zuletzt ${formatRelative(folder.latest_modified_at) || formatDate(folder.latest_modified_at)}` : ""}
        </p>
      </div>
    </button>
  );
}

function FolderView({
  folder,
  newDays,
  actions,
  onBack,
}: {
  folder: SchwarzesBrettFolder;
  newDays: number;
  actions: FileActions;
  onBack: () => void;
}) {
  const Icon = folderIconFor(folder.name);
  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <Icon className="h-5 w-5 text-accent" aria-hidden />
          {folder.name}
        </span>
      }
      description={folder.file_count === 1 ? "1 Datei" : `${folder.file_count} Dateien`}
      actions={
        <>
          {actions.canOpenInSharePoint && folder.web_url ? (
            <a
              href={folder.web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-sm text-label-secondary transition hover:bg-fill-tertiary hover:text-label"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
              Ordner in SharePoint
            </a>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            leftIcon={<ChevronLeftIcon className="h-4 w-4" aria-hidden />}
          >
            Alle Ordner
          </Button>
        </>
      }
      bodyClassName="px-2 py-2"
    >
      {folder.files.length ? (
        <FileList files={folder.files} newDays={newDays} showSubPath actions={actions} />
      ) : (
        <p className="px-3 py-6 text-center text-sm text-label-secondary">Dieser Ordner ist leer.</p>
      )}
    </Card>
  );
}

function SearchResults({
  groups,
  hits,
  newDays,
  actions,
  onOpenFolder,
}: {
  groups: Array<{ folder: SchwarzesBrettFolder | null; files: SchwarzesBrettFile[] }>;
  hits: number;
  newDays: number;
  actions: FileActions;
  onOpenFolder: (id: string) => void;
}) {
  if (!hits) {
    return (
      <Card>
        <p className="py-6 text-center text-sm text-label-secondary">Keine Dateien gefunden.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-label-secondary">
        {hits === 1 ? "1 Treffer" : `${hits} Treffer`}
      </p>
      {groups.map(({ folder, files }) => (
        <Card
          key={folder?.id || "loose"}
          title={folder?.name || "Weitere Dateien"}
          actions={
            folder ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenFolder(folder.id)}>
                Ordner öffnen
              </Button>
            ) : undefined
          }
          bodyClassName="px-2 py-2"
        >
          <FileList files={files} newDays={newDays} showSubPath actions={actions} />
        </Card>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------------ */

function FileList({
  files,
  newDays,
  showFolder = false,
  showSubPath = false,
  actions,
}: {
  files: SchwarzesBrettFile[];
  newDays: number;
  showFolder?: boolean;
  showSubPath?: boolean;
  actions: FileActions;
}) {
  return (
    <ul className="divide-y divide-separator">
      {files.map((file) => (
        <FileRow
          key={file.id}
          file={file}
          isNew={isRecentFile(file, newDays)}
          showFolder={showFolder}
          showSubPath={showSubPath}
          actions={actions}
        />
      ))}
    </ul>
  );
}

function FileRow({
  file,
  isNew,
  showFolder,
  showSubPath,
  actions,
}: {
  file: SchwarzesBrettFile;
  isNew: boolean;
  showFolder: boolean;
  showSubPath: boolean;
  actions: FileActions;
}) {
  const meta = fileKindMeta(file.extension);
  const printable = canPrintFile(file);
  const size = formatAttachmentSize(Number(file.size) || 0);
  const context = [
    showFolder ? file.folder_name : null,
    showSubPath || showFolder ? file.sub_path || null : null,
  ]
    .filter(Boolean)
    .join(" / ");
  const openLabel = meta.officeApp ? `In ${meta.officeApp} öffnen` : "In SharePoint öffnen";

  return (
    <li className="flex items-center gap-3 px-2 py-2 sm:px-3">
      <button
        type="button"
        onClick={() => actions.onOpen(file)}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-1 text-left transition hover:bg-fill-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        title={file.preview === "none" ? "Details anzeigen" : "Vorschau öffnen"}
      >
        <span
          className={clsx(
            "inline-flex h-9 w-12 shrink-0 items-center justify-center rounded-md text-[11px] font-bold uppercase tracking-wide",
            meta.badgeClass,
          )}
        >
          {meta.label}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="truncate text-sm font-medium text-label">{displayFileName(file.name)}</span>
            {isNew ? <NewBadge label="Neu" /> : null}
          </span>
          <span className="mt-0.5 block truncate text-xs text-label-secondary">
            {[
              context || null,
              file.last_modified_at
                ? `Geändert ${formatRelative(file.last_modified_at) || formatDate(file.last_modified_at)}`
                : null,
              file.last_modified_by || null,
              size || null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </span>
      </button>
      {printable ? (
        <button
          type="button"
          onClick={() => actions.onPrint(file)}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-label-secondary transition hover:bg-fill-tertiary hover:text-label focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          title="Drucken"
          aria-label={`${file.name}: Drucken`}
        >
          <PrinterIcon className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
      {actions.canOpenInSharePoint && file.web_url ? (
        <a
          href={file.web_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-label-secondary transition hover:bg-fill-tertiary hover:text-label"
          title={openLabel}
          aria-label={`${file.name}: ${openLabel}`}
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
        </a>
      ) : null}
    </li>
  );
}

function NewBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-status-green/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-status-green">
      {label}
    </span>
  );
}
